import { ValidatorFn } from '../../../config/validators';

type Value = string | number | undefined;

const isIterable = (obj: any) => {
  if (obj === undefined || obj === null) return false;
  return typeof obj[Symbol.iterator] === 'function';
};

export const sanitizeName = (name: string) => name.replace(/\./g, '-');

export namespace Expressions {
  export type Descriptor = { function: string } & { [param: string]: any };
  export type Truthy = true | 'true' | 1;
  export type Falsy = false | 'false' | 0;
  export type Boolean = Truthy | Falsy;
  export const isTrue = (v: unknown): v is Truthy =>
    v === true || v === 1 || v === 'true';
  export const isFalse = (v: unknown): v is Falsy =>
    v === false || v === 0 || v === 'false';
  export const isNull = (v: unknown) => !isTrue(v) && !isFalse(v);
  // TODO check how to use guards to enforce a string format
  export type StrDate = string; // formatted as \d{4}-\d{2}-\d{2}
  export type DateTime = string; // formatted as <date>T<time>
  export type Time = string; // formatted as \d{2}:\d{2}(:\d{2}(\.\d{3})?)?
  export type Temporal = StrDate | DateTime | Time;

  export type Fn<Args extends any[] = unknown[], R = any> = (
    ...props: Args
  ) => (...args: Parameters<ReturnType<ValidatorFn>>) => R;

  export type BooleanFn<Args extends any[] = unknown[]> = Fn<
    Args,
    Truthy | Falsy | null
  >;

  export type StringFn<Args extends any[] = unknown[]> = Fn<Args, string>;
  export type TemporalFn<Args extends any[] = unknown[]> = Fn<Args, Temporal>;

  export const temporalToDate = (temp: Temporal): Date => {
    try {
      return new Date(temp);
    } catch (e) {
      throw new Error(
        'could not parse the following temporal to a Date: ' + temp
      );
    }
  };

  namespace Logical {
    export type FromList = BooleanFn<[{ expr: Array<ReturnType<BooleanFn>> }]>;
    export type FromSingleExpr = BooleanFn<[{ expr: ReturnType<BooleanFn> }]>;
    export type Comparison<L extends Fn = Fn, R extends Fn = Fn> = BooleanFn<
      [{ left: ReturnType<L>; right: ReturnType<R> }]
    >;
    export type TemporalComparison = Comparison<TemporalFn, TemporalFn>;
  }

  const all: Logical.FromList =
    ({ expr }) =>
    (...args) => {
      return expr.every((e) => isTrue(e(...args)));
    };

  const any: Logical.FromList =
    ({ expr }) =>
    (...args) => {
      return expr.some((e) => isTrue(e(...args)));
    };

  const none: Logical.FromList =
    ({ expr }) =>
    (...args) => {
      return expr.every((e) => isFalse(e(...args)));
    };

  const not: Logical.FromSingleExpr =
    ({ expr }) =>
    (...args) => {
      const value = expr(...args);
      if (isTrue(value)) return false;
      if (isFalse(value)) return true;
      return null;
    };

  const greaterThan: Logical.Comparison =
    ({ left, right }) =>
    (...args) => {
      return left(...args) > right(...args);
    };

  const notGreaterThan: Logical.Comparison =
    ({ left, right }) =>
    (...args) =>
      not({ expr: greaterThan({ left, right }) })(...args);

  const lessThan: Logical.Comparison =
    ({ left, right }) =>
    (...args) => {
      return left(...args) < right(...args);
    };

  const lessThanOrEqualTo: Logical.Comparison =
    ({ left, right }) =>
    (...args) => {
      return left(...args) <= right(...args);
    };

  const greaterThanOrEqualTo: Logical.Comparison =
    ({ left, right }) =>
    (...args) => {
      return left(...args) >= right(...args);
    };

  const notLessThan: Logical.Comparison =
    ({ left, right }) =>
    (...args) =>
      not({ expr: lessThan({ left, right }) })(...args);

  const contains: BooleanFn<
    [{ value: ReturnType<Fn>; needle: ReturnType<Fn> }]
  > =
    ({ value, needle }) =>
    (...args) =>
      value(...args).includes(needle(...args));

  const notContains: typeof contains =
    ({ value, needle }) =>
    (...args) =>
      not({ expr: contains({ value, needle }) })(...args);

  const endsWith: BooleanFn<
    [{ value: ReturnType<StringFn>; tail: ReturnType<StringFn> }]
  > =
    ({ value, tail }) =>
    (...args) =>
      value(...args).endsWith(tail(...args));

  const notEndsWith: typeof endsWith =
    ({ value, tail }) =>
    (...args) =>
      not({ expr: endsWith({ value, tail }) })(...args);

  const beginsWith: BooleanFn<
    [{ value: ReturnType<StringFn>; head: ReturnType<StringFn> }]
  > =
    ({ value, head }) =>
    (...args) =>
      value(...args).startsWith(head(...args));

  const notBeginsWith: typeof beginsWith =
    ({ value, head }) =>
    (...args) =>
      not({ expr: beginsWith({ value, head }) })(...args);

  const getValue: Fn<[], Value> = () => (v) => v;

  const literal =
    <R = string | number>({ value }): ReturnType<Fn<[{ value: R }], R>> =>
    () =>
      value;
  const reference: Fn<[{ name: string }], Value> =
    ({ name }) =>
    (value, fields) => {
      // in order to be found here, fields must be declared in the injected fields
      if (!fields || !name) {
        return null;
      }

      if (fields.length === 0 && value) {
        return traverseObject(
          JSON.parse(typeof value === 'string' ? value : JSON.stringify(value)),
          name
        );
      }

      const ref = fields?.find((f) => f.name === sanitizeName(name));
      if (ref === undefined) {
        if (value === undefined) return false;
        return traverseObject(JSON.parse(JSON.stringify(value)), name);
      }
      return ref?.value;
    };

  const traverseObject = (obj, path) => {
    if (typeof obj !== 'object') return undefined;
    const keys = path.replace(/\[([^\]]+)]/g, '.$1').split('.');
    let current = obj;

    for (const key of keys) {
      if (current && key in current) {
        current = current[key];
      } else {
        return undefined;
      }
    }

    return current;
  };

  const durationBetween =
    ({ start, end, unit }) =>
    (...args) => {
      const unitVal = unit(...args);
      const startDate = Date.parse(start(...args));
      const endDate = Date.parse(end(...args));
      if (!startDate || !endDate) return null;
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);

      const yearsDiff = endDateObj.getFullYear() - startDateObj.getFullYear();
      const diffTime = endDate - startDate;
      switch (unitVal) {
        case 'year':
          return yearsDiff;
        case 'month':
          return (
            yearsDiff * 12 + (endDateObj.getMonth() - startDateObj.getMonth())
          );
        case 'week':
          return Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
        case 'day':
          return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        case 'hour':
          return Math.ceil(diffTime / (1000 * 60 * 60));
        case 'minute':
          return Math.ceil(diffTime / (1000 * 60));
        case 'second':
          return Math.ceil(diffTime / 1000);
        default:
          throw 'Something went wrong in the durationBetween parser';
      }
    };

  const isAfter: Logical.TemporalComparison =
    ({ left, right }) =>
    (...args) => {
      const leftDate = Date.parse(left(...args));
      const rightDate = Date.parse(right(...args));
      return leftDate > rightDate;
    };
  const isBefore: Logical.TemporalComparison =
    ({ left, right }) =>
    (...args) => {
      const leftDate = Date.parse(left(...args));
      const rightDate = Date.parse(right(...args));
      return leftDate < rightDate;
    };
  const isNotBefore: typeof isBefore =
    ({ left, right }) =>
    (...args) =>
      not({ expr: isBefore({ left, right }) })(...args);

  const prependZero = (str: string) => (str.length > 1 ? str : 0 + str);

  const today: Fn<[], StrDate> = () => () => {
    const date = new Date(Date.now());
    const month = prependZero(date.getMonth() + 1 + '');
    const day = prependZero(date.getDate() + '');
    return `${date.getFullYear()}-${month}-${day}`;
  };

  const getIf: Fn<
    [
      {
        condition: ReturnType<BooleanFn<any>>;
        then: ReturnType<Fn>;
        else: ReturnType<Fn>;
      }
    ]
  > =
    (params) =>
    (...args) => {
      return isTrue(params.condition(...args))
        ? params.then(...args)
        : params.else(...args);
    };

  // Check with Harry if it is supposed to be UTC datetime or custom runtime type DateTime
  const now: Fn<[], string> = () => () => new Date(Date.now()).toUTCString();
  // const now: Fn<[], string> = () => () => new Date(Date.now()).toISOString();

  const size: Fn<[{ value: ReturnType<Fn<unknown[], any>> }], number> =
    ({ value }) =>
    (...args) => {
      const actualValue = value(...args);

      if (isIterable(actualValue)) {
        return value(...args) !== undefined ? value(...args).length : 0;
      } else {
        return 0;
      }
    };

  const equalTo: Logical.Comparison =
    ({ left, right }) =>
    (...args) => {
      return left(...args) === right(...args);
    };

  const notEqualTo: Logical.Comparison =
    ({ left, right }) =>
    (...args) => {
      if (args[1] && args[1].length > 0) {
        return not({ expr: equalTo({ left, right }) })(...args);
      }
      return false;
    };
  const stringJoin: StringFn<
    [{ values: Array<ReturnType<StringFn>>; delimiter?: ReturnType<StringFn> }]
  > =
    ({ values, delimiter }) =>
    (...args) => {
      return values
        .map((v) => v(...args))
        .join(delimiter ? delimiter(...args) : ',');
    };

  export const implementations = {
    all,
    any,
    none,
    not,
    greaterThan,
    notGreaterThan,
    lessThan,
    lessThanOrEqualTo,
    greaterThanOrEqualTo,
    notLessThan,
    value: getValue,
    literal,
    contains,
    notContains,
    reference,
    endsWith,
    notEndsWith,
    beginsWith,
    notBeginsWith,
    durationBetween,
    isAfter,
    isBefore,
    isNotBefore,
    today,
    now,
    size,
    if: getIf,
    equalTo,
    notEqualTo,
    stringJoin,
  };

  export const parse = (descriptor: Descriptor): ReturnType<Fn> => {
    const fnName = descriptor.function;
    let fn: Fn;
    const args = {};
    if (
      Object.prototype.hasOwnProperty.call(implementations, fnName) &&
      typeof implementations[fnName] === 'function'
    ) {
      // get all attributes that are not the "function" attribute
      const expressionAttributes = Object.keys(descriptor).filter(
        (k) => k !== 'function'
      );
      fn = implementations[fnName];
      for (const attr of expressionAttributes) {
        const exprAttrValue = descriptor[attr];
        if (
          Array.isArray(exprAttrValue) &&
          exprAttrValue.every((subExpr) =>
            Object.prototype.hasOwnProperty.call(subExpr, 'function')
          )
        ) {
          // the attribute is an Array of Descriptors
          args[attr] = exprAttrValue.map((expr) => parse(expr));
        } else if (
          typeof exprAttrValue === 'object' &&
          Object.prototype.hasOwnProperty.call(exprAttrValue, 'function')
        ) {
          // the attribute is a Descriptor
          args[attr] = parse(exprAttrValue);
        } else {
          args[attr] = exprAttrValue;
        }
      }
    } else {
      console.warn(
        'could not find any expression definition corresponding to',
        fnName
      );
      // TODO: catch 1 level higher
      // note: if an expression is not found, the form is de facto invalid
      // Thus at the spot where the DynamicForm Validators are parsed,
      // the error should be caught so that the DynamicForm component shows an error
      throw new Error('Expression not found');
    }
    return fn(args);
  };

  const crawl = (
    expr: Descriptor,
    predicate: (expr: Descriptor) => boolean,
    action: (expr: Descriptor) => void
  ) => {
    if (!expr) return;
    if (predicate(expr)) action(expr);
    const attributes = Object.keys(expr);
    if (!attributes.includes('function')) return;
    for (const attr of attributes) {
      if (attr !== 'function') {
        const subExpr = expr[attr];
        if (typeof subExpr === 'object') crawl(subExpr, predicate, action);
        if (Array.isArray(subExpr))
          subExpr.forEach((se) => crawl(se, predicate, action));
      }
    }
  };

  export const getAll = (expr: Descriptor, fnName: string, key?: string) => {
    const refValues: string[] = [];
    crawl(
      expr,
      (e) => {
        return (
          Object.prototype.hasOwnProperty.call(e, 'function') &&
          e['function'] === fnName
        );
      },
      (e) => {
        refValues.push(key ? e[key] : e);
      }
    );
    return refValues.filter((v) => !!v);
  };

  export const getAllReferences = (expr: Descriptor, key?: string): string[] =>
    getAll(expr, 'reference', key);
}

export namespace Validators {
  import E = Expressions;

  export const get =
    (expr: ReturnType<E.BooleanFn>, message: string): ReturnType<ValidatorFn> =>
    (...args) =>
      E.isTrue(expr(...args)) ? undefined : [message];
}
